import React, { useState, useEffect } from 'react';
import { Table, Button, Card, Form, Row, Col, Badge, Modal, Dropdown } from 'react-bootstrap';
import { PlusIcon, EditIcon, TrashIcon, EyeIcon } from '../common/Icons';
import { invoiceService } from '../../services/api';
import { Invoice, PaginatedResponse } from '../../types';
import { toast } from 'react-toastify';
import InvoiceForm from './InvoiceForm';
import InvoiceDetails from './InvoiceDetails';
import StatusBadge from '../common/StatusBadge';

const InvoiceList: React.FC = () => {
  const [invoices, setInvoices] = useState<PaginatedResponse<Invoice> | null>(null);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [invoiceToDelete, setInvoiceToDelete] = useState<Invoice | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<number | null>(null);
  const [editingInvoice, setEditingInvoice] = useState<Invoice | null>(null);

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const params: any = {};
      if (typeFilter) params.type = typeFilter;
      if (statusFilter) params.status = statusFilter;

      const response = await invoiceService.getAll(params);
      setInvoices(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des factures');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoices();
  }, [typeFilter, statusFilter]);

  const handleDelete = async () => {
    if (!invoiceToDelete) return;

    try {
      await invoiceService.delete(invoiceToDelete.id);
      toast.success('Facture supprimée avec succès');
      fetchInvoices();
      setShowDeleteModal(false);
      setInvoiceToDelete(null);
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };



  const getTypeBadge = (type: string) => {
    const typeMap = {
      sale: { bg: 'success', text: 'Vente' },
      purchase: { bg: 'info', text: 'Achat' },
      credit_note: { bg: 'warning', text: 'Avoir' },
    };
    const config = typeMap[type as keyof typeof typeMap] || { bg: 'secondary', text: type };
    return <Badge bg={config.bg}>{config.text}</Badge>;
  };

  const openDetailsModal = (invoiceId: number) => {
    setSelectedInvoiceId(invoiceId);
    setShowDetailsModal(true);
  };

  const handleExportPdf = async (invoice: Invoice) => {
    try {
      const response = await invoiceService.generatePdf(invoice.id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `facture-${invoice.invoice_number}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Erreur lors de l\'export PDF');
    }
  };
    const handleExportPdfCachet = async (invoice: Invoice) => {
    try {
      const response = await invoiceService.generatePdfCachet(invoice.id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `facture-${invoice.invoice_number}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Erreur lors de l\'export PDF');
    }
  };

  const handleExportExcel = async (invoice: Invoice) => {
    try {
      const response = await invoiceService.generateExcel(invoice.id);
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `facture-${invoice.invoice_number}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Erreur lors de l\'export Excel');
    }
  };

  const handleStatusChange = async (invoice: Invoice, newStatus: string) => {
    try {
      await invoiceService.updateStatus(invoice.id, newStatus);
      toast.success('Statut mis à jour avec succès');
      fetchInvoices();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour du statut');
    }
  };

  const getStatusOptions = (currentStatus: string) => {
    const allStatuses = [
      { value: 'draft', label: 'Brouillon' },
      { value: 'sent', label: 'Envoyée' },
      { value: 'paid', label: 'Payée' },
      { value: 'partially_paid', label: 'Partiellement payée' },
      { value: 'overdue', label: 'En retard' },
      { value: 'cancelled', label: 'Annulée' },
    ];
    return allStatuses.filter(status => status.value !== currentStatus);
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1>Gestion des factures</h1>
        <Button variant="primary" onClick={() => setShowAddModal(true)}>
          <PlusIcon className="me-2" />
          Nouvelle facture
        </Button>
      </div>

      <Card>
        <Card.Header>
          <Row>
            <Col md={4}>
              <Form.Select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
              >
                <option value="">Tous les types</option>
                <option value="sale">Vente</option>
                <option value="purchase">Achat</option>
                <option value="credit_note">Avoir</option>
              </Form.Select>
            </Col>
            <Col md={4}>
              <Form.Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="">Tous les statuts</option>
                <option value="draft">Brouillon</option>
                <option value="sent">Envoyée</option>
                <option value="paid">Payée</option>
                <option value="partially_paid">Partiellement payée</option>
                <option value="overdue">En retard</option>
                <option value="cancelled">Annulée</option>
              </Form.Select>
            </Col>
          </Row>
        </Card.Header>
        <Card.Body>
          {loading ? (
            <div className="text-center">Chargement...</div>
          ) : (
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>Numéro</th>
                  <th>Type</th>
                  <th>Client/Fournisseur</th>
                  <th>Date</th>
                  <th>Échéance</th>
                  <th>Montant TTC</th>
                  <th>Reste à payer</th>
                  <th>Statut</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {invoices?.data.map((invoice) => (
                  <tr key={invoice.id}>
                    <td>
                      <Button 
                        variant="link" 
                        className="p-0 text-decoration-none"
                        onClick={() => openDetailsModal(invoice.id)}
                      >
                        {invoice.invoice_number}
                      </Button>
                    </td>
                    <td>{getTypeBadge(invoice.type)}</td>
                    <td>{invoice.client?.name || invoice.supplier?.name}</td>
                    <td>{new Date(invoice.date).toLocaleDateString('fr-FR')}</td>
                    <td>{new Date(invoice.due_date).toLocaleDateString('fr-FR')}</td>
                    <td>
                      {new Intl.NumberFormat('fr-FR', {
                        style: 'currency',
                        currency: 'MAD'
                      }).format(invoice.total_ttc)}
                    </td>
                    <td>
                      {new Intl.NumberFormat('fr-FR', {
                        style: 'currency',
                        currency: 'MAD'
                      }).format(invoice.remaining_amount)}
                    </td>
                    <td>
                      <Dropdown>
                        <Dropdown.Toggle variant="link" size="sm" className="p-0">
                          <StatusBadge status={invoice.status} type="invoice" />
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                          {getStatusOptions(invoice.status).map((status) => (
                            <Dropdown.Item
                              key={status.value}
                              onClick={() => handleStatusChange(invoice, status.value)}
                            >
                              {status.label}
                            </Dropdown.Item>
                          ))}
                        </Dropdown.Menu>
                      </Dropdown>
                    </td>
                    <td>
                      <Button 
                        variant="outline-info" 
                        size="sm" 
                        className="me-1"
                        onClick={() => openDetailsModal(invoice.id)}
                      >
                        <EyeIcon />
                      </Button>
                      <Button 
                        variant="outline-primary" 
                        size="sm" 
                        className="me-1"
                        onClick={() => {
                          setEditingInvoice(invoice);
                          setShowEditModal(true);
                        }}
                      >
                        <EditIcon />
                      </Button>
                      <Button 
                        variant="outline-success" 
                        size="sm" 
                        className="me-1"
                        onClick={() => handleExportPdf(invoice)}
                        title="Export PDF"
                      >
                        PDF
                      </Button>
                      <Button 
                        variant="outline-success" 
                        size="sm" 
                        className="me-1"
                        onClick={() => handleExportPdfCachet(invoice)}
                        title="Export PDF"
                      >
                        PDF Cachet
                      </Button>
                      
                      <Button 
                        variant="outline-warning" 
                        size="sm" 
                        className="me-1"
                        onClick={() => handleExportExcel(invoice)}
                        title="Export Excel"
                      >
                        XLS
                      </Button>
                      <Button 
                        variant="outline-danger" 
                        size="sm"
                        onClick={() => {
                          setInvoiceToDelete(invoice);
                          setShowDeleteModal(true);
                        }}
                      >
                        <TrashIcon />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirmer la suppression</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Êtes-vous sûr de vouloir supprimer la facture "{invoiceToDelete?.invoice_number}" ?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Annuler
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Supprimer
          </Button>
        </Modal.Footer>
      </Modal>

      <InvoiceDetails
        show={showDetailsModal}
        onHide={() => setShowDetailsModal(false)}
        invoiceId={selectedInvoiceId}
        onUpdate={fetchInvoices}
      />

      <InvoiceForm
        show={showAddModal}
        onHide={() => setShowAddModal(false)}
        onSuccess={fetchInvoices}
      />

      <InvoiceForm
        show={showEditModal}
        onHide={() => {
          setShowEditModal(false);
          setEditingInvoice(null);
        }}
        onSuccess={() => {
          fetchInvoices();
          setShowEditModal(false);
          setEditingInvoice(null);
        }}
        invoice={editingInvoice}
      />
    </div>
  );
};

export default InvoiceList;